import { enumObject, EnumObjectValue } from '@app/base';

export const EyeHealthSection = enumObject('EyeHealthSection', ['ANTERIOR', 'POSTERIOR'] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type EyeHealthSection = EnumObjectValue<typeof EyeHealthSection>;

export const EyeHealthInstrument = enumObject('EyeHealthInstrument', [
  'PORTABLE_SLIT_LAMP',
  'DIRECT_OPHTHALMOSCOPE',
  'INDIRECT_OPHTHALMOSCOPE',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type EyeHealthInstrument = EnumObjectValue<typeof EyeHealthInstrument>;

export const AnteriorEyeHealthInstrument = enumObject('AnteriorEyeHealthInstrument', [
  'PORTABLE_SLIT_LAMP',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AnteriorEyeHealthInstrument = EnumObjectValue<typeof AnteriorEyeHealthInstrument>;

export const PosteriorEyeHealthInstrument = enumObject('PosteriorEyeHealthInstrument', [
  'DIRECT_OPHTHALMOSCOPE',
  'INDIRECT_OPHTHALMOSCOPE',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PosteriorEyeHealthInstrument = EnumObjectValue<typeof PosteriorEyeHealthInstrument>;

export const InvDrops = enumObject('InvDrops', [
  'TROPICAMIDE_0_5',
  'TROPICAMIDE_1',
  'PHENYLEPHRINE_2_5',
  'CYCLOPENTOLATE_1',
  'FLUORESCEIN_2',
  'PROPARACAINE_0_5',
  'BENOXINATE_0_4',
  'TETRACAINE_1',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type InvDrops = EnumObjectValue<typeof InvDrops>;

export interface EyeHealthFieldRequest {
  leftEye: string | null;
  rightEye: string | null;
}

export interface AnteriorEyeHealthParametersRequest {
  pupilsRapd: EyeHealthFieldRequest;
  lidsLashes: EyeHealthFieldRequest;
  conjunctiva: EyeHealthFieldRequest;
  sclera: EyeHealthFieldRequest;
  cornea: EyeHealthFieldRequest;
  iris: EyeHealthFieldRequest;
  ac: EyeHealthFieldRequest;
  acAngle: EyeHealthFieldRequest;
  lens: EyeHealthFieldRequest;
  additional: EyeHealthFieldRequest;
}

export interface PosteriorEyeHealthParametersRequest {
  vitreous: EyeHealthFieldRequest;
  onh: EyeHealthFieldRequest;
  cdRatio: EyeHealthFieldRequest;
  vessels: EyeHealthFieldRequest;
  avRatio: EyeHealthFieldRequest;
  macula: EyeHealthFieldRequest;
  periphery: EyeHealthFieldRequest;
  additional: EyeHealthFieldRequest;
}

export interface EyeHealthRequest {
  invDrops: string | null;
  bn: string | null;
  expiryDate: string | null;
  dropsTakenAt: string | null;
  anterior: {
    instrument: AnteriorEyeHealthInstrument | null;
    parameters: AnteriorEyeHealthParametersRequest;
  } | null;
  posterior: {
    instrument: PosteriorEyeHealthInstrument | null;
    parameters: PosteriorEyeHealthParametersRequest;
  } | null;
}

export const PupilsRapd = enumObject('PupilsRapd', [
  'DCN',
  'PERRL',
  'RAPD',
  'MIOSIS',
  'FIXED',
  'ADIES_PUPIL',
  'HORNERS_SYNDROME',
  'ARGYLL_ROBERTSON_PUPIL',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PupilsRapd = EnumObjectValue<typeof PupilsRapd>;

export const LidsLashes = enumObject('LidsLashes', [
  'CLEAR',
  'ANTERIOR_BLEPHARITIS',
  'MGD',
  'ECTROPION',
  'ENTROPION',
  'CHALAZION',
  'EXTERNAL_HORDEOLUM_STYE',
  'INTERNAL_HORDEOLUM_CYST_OF_MOLL',
  'CYST_OF_ZEISS',
  'PTOSIS',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type LidsLashes = EnumObjectValue<typeof LidsLashes>;

export const Conjunctiva = enumObject('Conjunctiva', [
  'CLEAR',
  'HYPERAEMIA',
  'PTERYGIUM',
  'PINGUECULA',
  'SUBCONJUNCTIVAL_HEMORRHAGE',
  'PAPILLAE',
  'FOLLICLES',
  'CHEMOSIS',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Conjunctiva = EnumObjectValue<typeof Conjunctiva>;

export const Cornea = enumObject('Cornea', [
  'CLEAR',
  'ABRASION',
  'ULCER',
  'KERATIC_PRECIPITATES',
  'OEDEMATOUS',
  'OPAQUE',
  'ARCUS_SENILIS',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Cornea = EnumObjectValue<typeof Cornea>;

export const Sclera = enumObject('Sclera', ['CLEAR', 'EPISCLERITIS', 'SCLERITIS', 'THINNING'] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Sclera = EnumObjectValue<typeof Sclera>;

export const Iris = enumObject('Iris', [
  'CLEAR',
  'IRIDOTOMY',
  'POSTERIOR_SYNECHIAE',
  'TRANSILLUMINATION',
  'RUBEOSIS',
  'COLOBOMA',
  'DIALYSIS',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Iris = EnumObjectValue<typeof Iris>;

export const AC = enumObject('AC', ['CLEAR', 'DEEP_QUIET', 'SHALLOW', 'CELLS', 'FLARE'] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AC = EnumObjectValue<typeof AC>;

export const ACAngle = enumObject('ACAngle', ['VAN_HERICK_GRADE', 'SHADOW_GRADE'] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ACAngle = EnumObjectValue<typeof ACAngle>;

export const Lens = enumObject('Lens', [
  'CLEAR',
  'EARLY_LENS_CHANGES',
  'CATARACT',
  'NS',
  'CORTICAL',
  'PSC',
  'MIXED',
  'IOL',
  'PCO',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Lens = EnumObjectValue<typeof Lens>;

export const Vitreous = enumObject('Vitreous', [
  'CLEAR',
  'FLOATERS',
  'SHAFERS_NEGATIVE',
  'SHAFERS_POSITIVE',
  'WEISS_RING',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Vitreous = EnumObjectValue<typeof Vitreous>;

export const ONH = enumObject('ONH', [
  'CLEAR',
  'GLIMPSES',
  'FLAT_DISTINCT_MARGINS_GOOD_PALLOR',
  'GLAUCOMATOUS',
  'SWOLLEN',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ONH = EnumObjectValue<typeof ONH>;

export const CDRatio = enumObject('CDRatio', [
  '0_1',
  '0_2',
  '0_3',
  '0_4',
  '0_5',
  '0_6',
  '0_7',
  '0_8',
  '0_9',
  '1_0',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type CDRatio = EnumObjectValue<typeof CDRatio>;

export const Vessels = enumObject('Vessels', ['CLEAR', 'TORTUOUS'] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Vessels = EnumObjectValue<typeof Vessels>;

export const AVRatio = enumObject('AVRatio', [
  'ONE_QUARTER',
  'ONE_THIRD',
  'ONE_HALF',
  'TWO_THIRDS',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AVRatio = EnumObjectValue<typeof AVRatio>;

export const Macula = enumObject('Macula', [
  'CLEAR',
  'GLIMPSES',
  'RED_REFLEX_SEEN',
  'FLAT',
  'DRUSEN',
  'DRY_AMD',
  'WET_AMD',
  'DR',
  'HEMORRHAGE_HOLE',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Macula = EnumObjectValue<typeof Macula>;

export const Periphery = enumObject('Periphery', [
  'CLEAR',
  'PERIPHERAL_RETINAL_DEGENERATION',
  'NO_TEARS_HOLES_DETACHMENTS',
  'DRUSEN',
  'DR',
  'HAEMORRHAGE',
  'PIGMENTED_FUNDUS_LESION',
] as const);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Periphery = EnumObjectValue<typeof Periphery>;
